import {
  IconActivity,
  IconAlertSquareRoundedFilled,
  IconCheck,
  IconCircleDashed,
  IconFlameFilled,
  IconPlayerPauseFilled,
  IconTimeDurationOff,
} from "@tabler/icons-react";

import {
  DeviceType,
  MonitorStateStatus,
  MonitorStatus,
} from "@oneleet/api_client";
import { getReadableText } from "@oneleet/utils";

import { Badge, MonitorStatusIndicator, NgBadge } from "@oneleet/components";
import {
  CustomDeviceEncryptionIcon,
  CustomFirewallIcon,
  CustomGatekeeperIcon,
  CustomScreenlockIcon,
} from "@oneleet/components/src/icons";

export const MONITOR_TYPES: Record<
  string,
  {
    icon: React.ReactElement<{
      className?: string;
    }>;
    targetOses: DeviceType[];
  }
> = {
  oneleetMdmDeviceDormant_v1: {
    icon: <IconActivity className="text-neutral-550 size-4 shrink-0" />,
    targetOses: [DeviceType.MACOS, DeviceType.WINDOWS, DeviceType.LINUX],
  },
  oneleetMdmDeviceSessionLocking_v1: {
    icon: <CustomScreenlockIcon className="shrink-0" />,
    targetOses: [DeviceType.MACOS, DeviceType.WINDOWS],
  },
  oneleetMdmDeviceDiskEncryptionEnabled_v1: {
    icon: <CustomDeviceEncryptionIcon className="shrink-0" />,
    targetOses: [DeviceType.MACOS, DeviceType.WINDOWS, DeviceType.LINUX],
  },
  oneleetMdmDeviceFirewallControlsEnabled_v1: {
    icon: <CustomFirewallIcon className="shrink-0" />,
    targetOses: [DeviceType.MACOS, DeviceType.WINDOWS],
  },
  oneleetMdmDeviceGatekeeperEnabled_v1: {
    icon: <CustomGatekeeperIcon className="shrink-0" />,
    targetOses: [DeviceType.MACOS],
  },
};

export const MONITOR_STATUSES: Record<
  MonitorStatus,
  {
    label: string;
    badgeColor: Parameters<typeof NgBadge>["0"]["color"];
    badgeVariant: Parameters<typeof NgBadge>["0"]["variant"];
    legacyBadgeColor: Parameters<typeof Badge>["0"]["color"];
    icon: React.ReactElement<{
      className?: string;
    }>;
    symbolIcon: React.ReactElement<{
      className?: string;
    }>;
    order: number;
  }
> = {
  [MonitorStatus.BREACHING_SLA]: {
    label: "Breaching SLA",
    badgeColor: "red",
    badgeVariant: "solid",
    legacyBadgeColor: "red",
    icon: (
      <MonitorStatusIndicator monitorStatus={MonitorStatus.BREACHING_SLA} />
    ),
    symbolIcon: <IconFlameFilled className="text-ng-red-800" />,
    order: 0,
  },
  [MonitorStatus.ALERTING]: {
    label: getReadableText(MonitorStatus.ALERTING),
    badgeColor: "orange",
    badgeVariant: "outlined",
    legacyBadgeColor: "orange",
    icon: <MonitorStatusIndicator monitorStatus={MonitorStatus.ALERTING} />,
    symbolIcon: <IconAlertSquareRoundedFilled className="text-ng-orange-800" />,
    order: 1,
  },
  [MonitorStatus.PASSING]: {
    label: getReadableText(MonitorStatus.PASSING),
    badgeColor: "green",
    badgeVariant: "outlined",
    legacyBadgeColor: "green",
    icon: <MonitorStatusIndicator monitorStatus={MonitorStatus.PASSING} />,
    symbolIcon: <IconCheck className="text-ng-green-900" />,
    order: 2,
  },
  [MonitorStatus.NO_APPLICABLE_ASSETS]: {
    label: getReadableText(MonitorStatus.NO_APPLICABLE_ASSETS),
    badgeColor: "neutral",
    badgeVariant: "outlined",
    legacyBadgeColor: "gray",
    icon: (
      <MonitorStatusIndicator
        monitorStatus={MonitorStatus.NO_APPLICABLE_ASSETS}
      />
    ),
    symbolIcon: <IconTimeDurationOff className="text-neutral-500" />,
    order: 3,
  },
  [MonitorStatus.NOT_YET_RUN]: {
    label: "Pending",
    icon: <MonitorStatusIndicator monitorStatus={MonitorStatus.NOT_YET_RUN} />,
    badgeColor: "accent",
    badgeVariant: "outlined",
    legacyBadgeColor: "gray",
    symbolIcon: <IconCircleDashed className="text-accent-800" />,
    order: 4,
  },
  [MonitorStatus.IGNORED]: {
    label: getReadableText(MonitorStatus.IGNORED),
    badgeColor: "neutral",
    badgeVariant: "subtle",
    legacyBadgeColor: "gray",
    icon: <MonitorStatusIndicator monitorStatus={MonitorStatus.IGNORED} />,
    symbolIcon: <IconTimeDurationOff className="text-neutral-500" />,
    order: 5,
  },
  [MonitorStatus.DISABLED]: {
    label: getReadableText(MonitorStatus.DISABLED),
    badgeColor: "neutral",
    badgeVariant: "subtle",
    legacyBadgeColor: "gray",
    icon: <MonitorStatusIndicator monitorStatus={MonitorStatus.DISABLED} />,
    symbolIcon: <IconPlayerPauseFilled className="text-neutral-500" />,
    order: 6,
  },
};

export const MONITOR_STATE_STATUSES: Record<
  MonitorStateStatus,
  {
    label: string;
    legacyBadgeColor: Parameters<typeof Badge>["0"]["color"];
  }
> = {
  [MonitorStateStatus.BREACHING_SLA]: {
    label: "Breaching SLA",
    legacyBadgeColor: "red",
  },
  [MonitorStateStatus.ALERTING]: {
    label: "Alerting",
    legacyBadgeColor: "orange",
  },
  [MonitorStateStatus.PASSING]: {
    label: "Passing",
    legacyBadgeColor: "green",
  },
  [MonitorStateStatus.INACTIVE]: {
    label: "Inactive",
    legacyBadgeColor: "gray",
  },
};
